import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNil } from "lodash";

import supplierService from "@plannel/services/data/supplier-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import dateUtils from "@plannel/utils/date-util";
import isEmail from "validator/lib/isEmail";
import CodeUtil from "@plannel/pages/utils/code-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;

const SupplierMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  const searchCode = useRef(reduxViewState?.searchCode ?? "");
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [supplierData, setSupplierData] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailValidation = (e1) => {
    if(isEmail(e1?.email)){
      return true;
    }

    setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${e1?.email ?? " "}] ${t("MSG_InvalidEmailFormat")}` });
    return false;
  }

  const columnDefs = [
    {
      headerName: "supplierCd", field: "supplierCd", colId: "supplierCd",
      cellClass: 'stringType',
      width: 200, filterType: 'string'
    },
    {
      headerName: "supplierName", field: "name", colId: "supplierName",
      width: 200, filterType: 'string'
    },
    { headerName: "telNumber", field: "telNum", width: 280, filterType: 'string' },
    { headerName: "email", field: "email", width: 200, filterType: 'string' },
    { headerName: "address", field: "address", width: 120, filterType: 'string' },
    { headerName: "descTxt", field: "descTxt", width: 120, filterType: 'string' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 120, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (searchCode.current) {
      searchFilters.supplierCd = searchCode.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveSupplier = () => {
    const params = getRequestParams();
    
    supplierService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;

        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setSupplierData(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { searchCode: searchCode.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const removeSupplier = () => {
    const supplierIds = [];
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    for (const data of selectedRows) {
      if (isNil(data.id)) {
        continue;
      }

      supplierIds.push(data.id);
    }

    if (supplierIds.length === 0 && selectedRows.length > 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.confirm, content: t("MSG_DeleteSuccess") });

      retrieveSupplier();
      return;
    }

    if (supplierIds.length > 0) {
      supplierService.remove(supplierIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.confirm, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveSupplier();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_DeleteNoData") });
    }

  }

  const saveSupplier = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    
    let emailValid = true;
    if (!isNil(allStateData)){
      for (const data of allStateData) {
        if (!isNil(data.email) && !isEmpty(data.email)) {
          if (!emailValidation(data)) {
            emailValid = false;
            break;
          }
        }
      }
    }

    if (!emailValid) {
      return;
    }

    if (allStateData?.length > 0) {
      const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};
      
      const dataWithFlg = allStateData.map((row) => {
        const originalData = updatedOrigin[row.rowIndex];
        let activeFlgChanged = false;
        if (originalData) {
          activeFlgChanged = originalData?.activeFlg !== row?.activeFlg;
        }
        
        return {
          ...row,
          activeFlgChanged
        };
      });
      
      supplierService.upsert(dataWithFlg)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          retrieveSupplier();
        });
      

    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const checkActiveFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};
    
    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.activeFlg !== row.activeFlg && row.activeFlg === false;
      }
      return false;
    });
  };

  const handleSaveClick = () => {
    gridRef.current?.api?.stopEditing();
    
    const hasActiveFlgChanges = checkActiveFlgChanges();
    
    if (hasActiveFlgChanges) {
      setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.supplier, 'deactivate')}` }); 
    } else {  
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveSupplier();
    } else if (action === "DELETE") {
      removeSupplier();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveSupplier();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveSupplier();
    }

    return () => {
      setSupplierData([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const addRow = () => {
    const newRow = {
      id: null,
      suppliercd: null,
      name: null,
      telNum: null,
      email: null,
      address: null,
      descTxt: null,
      activeFlg: true,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }
  
  return (
    <>
      <Box sx={{ display: "flex" }}>
        <TextFieldSearch sx={{ my: 1, width: 250 }} label={"supplierCd"} searchCode={searchCode} retrieve={initPageToRetrieve} />
        <Box sx={{ mt: 1.5, ml: "auto", mr: 1.5 }}>
          <ExcelExportButton title={title} service={supplierService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.supplier, 'delete')}` })} />
          <SaveButton onClick={handleSaveClick} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </Box>
      <Box id="supplier-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={supplierData}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(SupplierMaster);